import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { FaHeart, FaStar, FaShoppingCart, FaPlus, FaMinus } from "react-icons/fa";
import toast from "react-hot-toast";
import { getImageUrl } from "../BASEURL";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const [cartQty, setCartQty] = useState(0);

  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem("currentUser"));
    } catch {
      return null;
    }
  };

  const getCartKey = (user) => {
    const email = user?.email || user?.emailId;
    return email ? `cartItems_${email}` : "cartItems_guest";
  };

  useEffect(() => {
    const syncCartQty = () => {
      const user = getCurrentUser();
      const cartKey = getCartKey(user);
      const cart = JSON.parse(localStorage.getItem(cartKey)) || [];
      const item = cart.find((i) => String(i._id) === String(product._id));
      setCartQty(item ? item.quantity : 0);
    };
    syncCartQty();
    window.addEventListener("cartUpdated", syncCartQty);
    return () => window.removeEventListener("cartUpdated", syncCartQty);
  }, [product._id]);

  const toastStyle = {
    position: "bottom-right",
    style: {
      background: "#1f2937",
      color: "#fff",
      borderRadius: "10px",
    },
  };

  const goToDetail = () => {
    navigate(`/product/${product._id}`);
  };

  // ❤️ Wishlist
  const addToWishlist = (e) => {
    e.stopPropagation();

    const wishlist =
      JSON.parse(localStorage.getItem("wishlistItems")) || [];

    const exists = wishlist.some((item) => item._id === product._id);

    if (exists) {
      toast("Already in wishlist ❤️", {
        ...toastStyle,
        icon: "ℹ️",
      });
      return;
    }

    wishlist.push(product);
    localStorage.setItem("wishlistItems", JSON.stringify(wishlist));

    toast.success("Added to wishlist ❤️", toastStyle);
  };



  const updateCartQuantity = (newQty, e) => {
    e?.stopPropagation();
    const user = getCurrentUser();
    const cartKey = getCartKey(user);
    let cart = JSON.parse(localStorage.getItem(cartKey)) || [];
    const index = cart.findIndex((item) => String(item._id) === String(product._id));

    if (newQty <= 0) {
      if (index > -1) cart.splice(index, 1);
      setCartQty(0);
    } else if (index > -1) {
      cart[index].quantity = newQty;
      setCartQty(newQty);
    } else {
      cart.push({
        _id: product._id,
        name: product.productName,
        price:
          product.sizes?.[0]?.price ||
          product.price ||
          0,

        discountPrice:
          product.sizes?.[0]?.discountPrice ||
          product.discountPrice ||
          product.price ||
          0,

        image: product.images?.[0] || product.image,
        quantity: newQty,
      });

      setCartQty(newQty);
    }

    localStorage.setItem(cartKey, JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const addToCart = (e) => {
    e.stopPropagation();
    updateCartQuantity(cartQty > 0 ? cartQty + 1 : 1, e);
    toast.success("Added to cart 🛒", toastStyle);
  };

  const discount = (() => {
    const currentPrice = Number(
      product.sizes?.[0]?.discountPrice ??
      product.sizes?.[0]?.price ??
      product.discountPrice ??
      product.price ??
      0
    );
    const originalPrice = Number(
      product.sizes?.[0]?.price ?? product.price ?? 0
    );
    if (!originalPrice || currentPrice >= originalPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  })();

  return (
    <div
      onClick={goToDetail}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer flex flex-col h-full"
    >
      {/* IMAGE */}
      <div className="relative h-52 sm:h-60 md:h-64 overflow-hidden bg-gray-50">

        {/* Discount badge */}
        {discount > 0 && (
          <span className="absolute top-3 left-3 z-10 bg-orange-600 text-white text-[10px] px-2 py-1 rounded-full font-bold">
            {discount}% OFF
          </span>
        )}

        <img
          src={getImageUrl(product.images?.[0] || product.image)}
          alt={product.productName}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-contain group-hover:scale-110 transition-transform duration-700"
          onError={(e) => {
            e.target.src =
              "https://via.placeholder.com/300?text=No+Image";
          }}
        />
      </div>

      {/* CONTENT */}
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-sm sm:text-base md:text-lg text-gray-800 group-hover:text-orange-600 transition-colors line-clamp-1">
            {product.productName}
          </h3>

          <div className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-md shrink-0">
            <FaStar className="text-yellow-500 text-xs" />
            <span className="text-xs font-semibold text-orange-700">
              {product.rating || 4.2}
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider font-medium">
          {product.categoryId?.name || "Vastu Product"}
        </p>

        {/* PRICE + BUTTONS */}
        <div className="mt-auto flex items-center justify-between pt-4">
          <div className="flex flex-col">
            {product.sizes && product.sizes.length > 0 ? (
              <>
                {product.sizes[0].price && (
                  <span className="text-gray-400 line-through text-[10px] sm:text-xs">
                    ₹{product.sizes[0].price}
                  </span>
                )}
                <span className="text-base sm:text-lg font-bold text-black">
                  ₹{product.sizes[0].discountPrice}
                </span>
              </>
            ) : (
              <>
                {product.price && (
                  <span className="text-gray-400 line-through text-[10px] sm:text-xs">
                    ₹{product.price}
                  </span>
                )}
                <span className="text-base sm:text-lg font-bold text-black">
                  ₹{product.discountPrice}
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={addToWishlist}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-all"
              title="Add to Wishlist"
            >
              <FaHeart size={14} />
            </button>

            {cartQty > 0 ? (
              <div
                className="flex items-center gap-1 bg-black text-white rounded-full px-1 py-0.5 shadow-md"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={(e) => updateCartQuantity(cartQty - 1, e)}
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-all"
                  aria-label="Decrease quantity"
                >
                  <FaMinus size={10} />
                </button>
                <span className="text-xs sm:text-sm font-bold min-w-[1rem] text-center">
                  {cartQty}
                </span>
                <button
                  type="button"
                  onClick={(e) => updateCartQuantity(cartQty + 1, e)}
                  className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-all"
                  aria-label="Increase quantity"
                >
                  <FaPlus size={10} />
                </button>
              </div>
            ) : (
              <button
                onClick={addToCart}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-black text-white hover:bg-orange-600 transition-all shadow-md active:scale-95"
                title="Add to Cart"
              >
                <FaShoppingCart size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
